import React from 'react';
import type { TextEffectsSharedProps } from '../TextEffectsShared.types';
import {
  formatClassNames,
  getDataAttributes,
  getTabIndexAttribute,
} from '@wix/editor-elements-common-utils';
import {
  dataHooks,
  TestIds,
  DEFAULT_HTML_TAG,
  DEFAULT_TEXT_DIRECTION,
} from '../constants';
import cx from 'clsx';
import semanticClassNames from '../TextEffectsShared.semanticClassNames';
import Link from '@wix/thunderbolt-elements/components/Link';
import styles from './TextEffectsShared.scss';

const TextEffectsShared: React.FC<
  React.PropsWithChildren<TextEffectsSharedProps>
> = props => {
  const {
    id,
    className,
    text,
    customClassNames = [],
    link,
    onMouseEnter,
    onMouseLeave,
    children,
    textDirection = DEFAULT_TEXT_DIRECTION,
    isDecorative,
    tagName: Tag = DEFAULT_HTML_TAG,
    a11y,
    initialReducedMotion,
    forceReducedMotion,
    tagProps = {},
    arePatternsDisabled = true,
  } = props;
  const { animated, ...restTagProps } = tagProps;

  const [reducedMotion, setReducedMotion] =
    React.useState(initialReducedMotion);

  const resolvedReducedMotion = forceReducedMotion ?? reducedMotion;

  const [isMounted, setIsMounted] = React.useState(false);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  const toggleReducedMotion = () => {
    setReducedMotion(prevReducedMotion => !prevReducedMotion);
  };

  const isClickable = animated && !forceReducedMotion;

  return (
    <div
      {...(isDecorative && { 'aria-hidden': true })}
      id={id}
      data-hook={dataHooks.root}
      {...(isClickable && { onClick: toggleReducedMotion })}
      className={cx(
        className,
        formatClassNames(semanticClassNames.root, ...customClassNames),
        // reduced motion is false in SSR so it's respected only after the component is mounted
        // to avoid hydration mismatch
        { 'reduced-motion': isMounted && resolvedReducedMotion },
        { [styles.empty]: text === '' },
        styles.root,
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      dir={textDirection}
      {...getDataAttributes(props)}
      {...getTabIndexAttribute(a11y)}
    >
      {link ? (
        <Link
          {...link}
          className={cx(styles.link)}
          dataTestId={TestIds.viewer.link}
        >
          <Tag {...restTagProps}>
            {!arePatternsDisabled && text && (
              // Use b instead of span to avoid interfering with first-of-type selectors in PatternUnit.scss
              // Invisible spans were being counted in first-of-type calculations, causing wrong pattern targeting
              <b
                className={styles.visuallyHidden}
                data-testid={TestIds.viewer.visuallyHidden}
              >
                {text}
              </b>
            )}
            {children}
          </Tag>
        </Link>
      ) : (
        <Tag {...restTagProps}>
          {!arePatternsDisabled && text && (
            // Use b instead of span to avoid interfering with first-of-type selectors in PatternUnit.scss
            // Invisible spans were being counted in first-of-type calculations, causing wrong pattern targeting
            <b
              className={styles.visuallyHidden}
              data-testid={TestIds.viewer.visuallyHidden}
            >
              {text}
            </b>
          )}
          {children}
        </Tag>
      )}
    </div>
  );
};

export default TextEffectsShared;
